import {useCallback, useState, useMemo} from 'react';
import {NodeSelectionChange, EdgeSelectionChange} from 'reactflow';

import {ProcedureEdgeType, ProcedureFlowchart} from 'procedures/ProcedureDomainTypes';

import {
  deleteMethodFromLinearFlowchart,
  extractMaterialEdges,
  extractMethodNodes,
} from './flowchartManagementUtils';
import {ConvertedProcedureGraph} from './ProcedureReactFlowCanvasTypes';
import {ModifyFlowchartCallback} from './useProcedureActions';

export interface UseProcedureFlowchartEditorSelectionOutput {
  handleNodeSelectionChanges: (changes: Array<NodeSelectionChange>) => void;
  handleEdgeSelectionChanges: (changes: Array<EdgeSelectionChange>) => void;
  handleDeleteSelection: () => void;
  convertedProcedureGraphWithSelected: ConvertedProcedureGraph;
}

/**
 * Encapsulates all method and edge selection-related state and callbacks within a ProcedureEditor.
 * Multi-selection in Reactflow is triggered by holding the default Meta key while clicking on a node or edge.
 * For MacOS, the Meta key should default to the Command key unless overwritten elsewhere.
 *
 * Note - method edges are not selectable.
 */
export function useProcedureFlowchartEditorSelection(
  flowchart: ProcedureFlowchart,
  modifyFlowchart: (cb: ModifyFlowchartCallback) => void,
  convertedProcedureGraph: ConvertedProcedureGraph,
  stopEditMethod: () => void
): UseProcedureFlowchartEditorSelectionOutput {
  const [selectedMethodCids, setSelectedMethodCids] = useState<Array<string>>([]);
  const [selectedMaterialEdgeIds, setSelectedMaterialEdgeIds] = useState<Array<string>>([]);

  const handleNodeSelectionChanges = useCallback(
    (changes: Array<NodeSelectionChange>): void => {
      const methodCids = extractMethodNodes(flowchart.nodes).map(({cid}) => cid);
      const methodsToSelect = changes.filter((c) => c.selected).map(({id}) => id);
      const methodsToUnselect = changes.filter((c) => !c.selected).map(({id}) => id);
      setSelectedMethodCids(
        selectedMethodCids
          .filter((methodCid) => !methodsToUnselect.includes(methodCid))
          .concat(methodsToSelect)
          .filter((methodCid) => methodCids.includes(methodCid))
      );
    },
    [flowchart.nodes, selectedMethodCids]
  );

  const handleEdgeSelectionChanges = useCallback(
    (changes: Array<EdgeSelectionChange>): void => {
      const edgeIds = extractMaterialEdges(flowchart.edges).map(({id}) => id);
      const edgesToSelect = changes.filter((c) => c.selected).map(({id}) => id);
      const edgesToUnselect = changes.filter((c) => !c.selected).map(({id}) => id);
      setSelectedMaterialEdgeIds(
        selectedMaterialEdgeIds
          .filter((edgeId) => !edgesToUnselect.includes(edgeId))
          .concat(edgesToSelect)
          .filter((edgeId) => edgeIds.includes(edgeId))
      );
    },
    [flowchart.edges, selectedMaterialEdgeIds]
  );

  const handleDeleteSelectedNodes = useCallback(
    (fc: ProcedureFlowchart): ProcedureFlowchart => {
      stopEditMethod();
      const nodesAfterDeletion = fc.nodes.filter((node) => !selectedMethodCids.includes(node.cid));

      if (nodesAfterDeletion.length < fc.nodes.length) {
        const deletedNodes = fc.nodes.filter((node) => selectedMethodCids.includes(node.cid));
        deletedNodes.forEach((deletedNode) => {
          fc = deleteMethodFromLinearFlowchart(fc, deletedNode.cid);
        });
        setSelectedMethodCids([]);
      }
      return fc;
    },
    [selectedMethodCids, stopEditMethod]
  );

  const handleDeleteSelectedEdges = useCallback(
    (fc: ProcedureFlowchart): ProcedureFlowchart => {
      const edgesAfterDeletion = fc.edges.filter(
        (edge) => !(selectedMaterialEdgeIds.includes(edge.id) && edge.type !== ProcedureEdgeType.METHOD_EDGE)
      );
      if (edgesAfterDeletion.length < fc.edges.length) {
        setSelectedMaterialEdgeIds([]);
        return {nodes: fc.nodes, edges: edgesAfterDeletion};
      }
      return fc;
    },
    [selectedMaterialEdgeIds]
  );

  const handleDeleteSelection = useCallback((): void => {
    modifyFlowchart((fc) => handleDeleteSelectedEdges(handleDeleteSelectedNodes(fc)));
  }, [handleDeleteSelectedEdges, handleDeleteSelectedNodes, modifyFlowchart]);

  const canvasNodesWithSelected = useMemo(
    () =>
      convertedProcedureGraph.nodes.map((node) => ({
        ...node,
        selected: selectedMethodCids.includes(node.id),
      })),
    [convertedProcedureGraph.nodes, selectedMethodCids]
  );

  const canvasEdgesWithSelected = useMemo(
    () =>
      convertedProcedureGraph.edges.map((edge) => ({
        ...edge,
        selected: selectedMaterialEdgeIds.includes(edge.id),
      })),
    [convertedProcedureGraph.edges, selectedMaterialEdgeIds]
  );

  return {
    handleNodeSelectionChanges,
    handleEdgeSelectionChanges,
    handleDeleteSelection,
    convertedProcedureGraphWithSelected: {nodes: canvasNodesWithSelected, edges: canvasEdgesWithSelected},
  };
}
